import React, { useState } from "react";
import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
} from "react-icons/hi";

import { apiUrl, brands } from "../config/config";
import SEO from "../components/SEO";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    brand: brands,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Something went wrong");
      }

      setSuccess("Thank you! Your message has been sent successfully.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        brand: brands,
      });
    } catch (err) {
      setError(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Shirts Patches"
        keywords="Shirts Patches"
      />

      <div className="min-h-screen bg-lightPastelPurple">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          {/* Header */}
          <div className="relative overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-12 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10 md:py-16">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative mx-auto max-w-4xl">
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-6xl">
                Contact Us
              </h1>

              <div className="mx-auto mt-4 flex w-full max-w-[230px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>

              <p className="mx-auto mt-6 max-w-3xl text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                Have questions or need assistance? Reach out to our team at{" "}
                <span className="font-extrabold text-darkPurple">
                  Personalised Hat Patches
                </span>
                .
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            {/* Contact Info */}
            <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-darkPurple p-6 text-white shadow-[0_18px_45px_rgba(47,34,84,0.16)] md:p-8 lg:p-10">
              <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pastelPurple/30 blur-3xl"></div>
              <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-keyLime/25 blur-3xl"></div>

              <div className="relative">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-white md:text-3xl">
                  Get in Touch
                </h2>

                <div className="mt-4 h-1 w-20 rounded-full bg-keyLime"></div>

                <div className="mt-8 space-y-4">
                  <p className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-medium leading-7 text-[#f2ecff]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-keyLime text-darkPurple">
                      <HiOutlineMail className="h-6 w-6" />
                    </span>
                    support@shirtspatches.com
                  </p>

                  <p className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-medium leading-7 text-[#f2ecff]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-keyLime text-darkPurple">
                      <HiOutlinePhone className="h-6 w-6" />
                    </span>
                    (703) 666-8193
                  </p>

                  {/* <p className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-medium leading-7 text-[#f2ecff]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-keyLime text-darkPurple">
                      <HiOutlineLocationMarker className="h-6 w-6" />
                    </span>
                    Los Angeles, CA
                  </p> */}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8 lg:p-10">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <div className="relative">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                  Send Us a Message
                </h2>

                <div className="mt-4 flex items-center gap-3">
                  <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                  <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                </div>

                {success && (
                  <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 px-5 py-4 text-sm font-bold text-green-700">
                    {success}
                  </div>
                )}

                {error && (
                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  {["name", "email", "subject"].map((field) => (
                    <div key={field}>
                      <label className="mb-2 block text-sm font-extrabold capitalize text-darkPurple">
                        {field}
                      </label>

                      <input
                        type={field === "email" ? "email" : "text"}
                        name={field}
                        value={formData[field]}
                        onChange={handleChange}
                        required
                        className="w-full rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-3.5 text-base font-medium text-darkPurple outline-none transition focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60"
                      />
                    </div>
                  ))}

                  <div>
                    <label className="mb-2 block text-sm font-extrabold text-darkPurple">
                      Message
                    </label>

                    <textarea
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full resize-none rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-3.5 text-base font-medium text-darkPurple outline-none transition focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-2xl bg-keyLime px-7 py-3.5 text-center text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default Contact;